// Load Google Fonts dynamically to avoid esbuild font inlining issues
const fontLink = document.createElement('link');
fontLink.rel = 'stylesheet';
fontLink.href = 'https://fonts.googleapis.com/css?family=Open+Sans:300,400,600,700&lang=en';
document.head.appendChild(fontLink);

// Lightweight imports only
import "html2canvas";
import * as CryptoJS from 'crypto-js';
import * as _ from 'lodash';

// Lazy load heavy libraries
// Chart.js, Fabric.js are loaded on-demand via lazy loaders in components
// Bootstrap CSS is loaded via angular.json styles array - JS plugins removed (no jQuery dependency)

import html2canvas from "html2canvas";
// Lazy load PDF export
const loadPdfMake = () => {
    return Promise.all([
        import('pdfmake/build/pdfmake.js'),
        import('pdfmake/build/vfs_fonts.js')
    ]).then(([pdfMakeModule, pdfFontsModule]) => {
        const pdfMake = (pdfMakeModule as any).default || pdfMakeModule;
        const pdfFonts = (pdfFontsModule as any).default || pdfFontsModule;
        // pdfmake 0.3: vfs_fonts exports the vfs itself and it is registered, not assigned
        pdfMake.addVirtualFileSystem(pdfFonts);
        return pdfMake;
    });
};

// Lazy load Excel export
const loadExcelBuilder = () => {
    return import('excel-builder-webpacker').then(module => {
        const ExcelBuilder = (module as any).default || module;
        (window as any).ExcelBuilder = ExcelBuilder;
        return ExcelBuilder;
    });
};

// Lazy load BeeFree SDK
const loadBeeFree = () => import('@beefree.io/sdk');

// Expose lazy loaders globally
(window as any).loadPdfMake = loadPdfMake;
(window as any).loadExcelBuilder = loadExcelBuilder;
(window as any).loadBeeFree = loadBeeFree;

// Lazy load OpenTelemetry after app boot to reduce initial bundle
import('./instrument');

// Angular bootstrap
import { bootstrapApplication } from "@angular/platform-browser";
import { provideHttpClient, withInterceptorsFromDi, withInterceptors } from "@angular/common/http";
import { provideRouter, withRouterConfig, withInMemoryScrolling } from "@angular/router";
import { importProvidersFrom, APP_INITIALIZER, ErrorHandler } from "@angular/core";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideOptimus, Optimus } from "@openng/optimus-ui/config";
import { Table, TableService } from "@openng/optimus-ui/table";
import { MessageService, ConfirmationService } from "@openng/optimus-ui/api";
import { TranslateModule, TranslateService, provideTranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import esLocale from "@openng/optimus-ui-locale/es.json";
import { StelaPreset } from './styles/design-tokens/stela.preset';

import { AppComponent } from "./app/app.component";
import { routes } from "./app/app.routes";
import { authInterceptor } from "./app/core/interceptors/auth.interceptor";
import { errorInterceptor } from "./app/core/interceptors/error.interceptor";

// Providers migrated from removed NgModules
import { DatePipe } from "@angular/common";
import { GlobalErrorHandler } from "./app/core/error-handler/global-error-handler";
import { ContrastColorPipe } from "./app/shared/pipes/contrast-color.pipe";
import { FullnamePipe } from "./app/shared/pipes/fullname.pipe";
import { GridColumnDataPipe } from "./app/shared/pipes/grid-column-data.pipe";
import { GuestAccessStatusPipe } from "./app/features/events/pipes/guest-access-status.pipe";
import { GuestAccessStatusColorPipe } from "./app/features/events/pipes/guest-access-status-color.pipe";
import { GuestConfirmationStatusTagPipe } from "./app/features/events/pipes/guest-confirmation-status-tag.pipe";
import { GuestGroupFullnamePipe } from "./app/features/events/pipes/guest-group-fullname.pipe";

function initializeApp(primeng: Optimus, translateService: TranslateService) {
    return () => {
        translateService.setDefaultLang('es');
        primeng.setTranslation(esLocale.es);
        primeng.zIndex = {
            modal: 1100,
            overlay: 1200,
            menu: 1200,
            tooltip: 1300
        };
    };
}

bootstrapApplication(AppComponent, {
    providers: [
        provideRouter(routes, withRouterConfig({ onSameUrlNavigation: 'reload', paramsInheritanceStrategy: 'always' }), withInMemoryScrolling({ scrollPositionRestoration: 'top' })),
        provideHttpClient(withInterceptorsFromDi(), withInterceptors([errorInterceptor, authInterceptor])),
        provideAnimationsAsync(),
        provideOptimus({
            theme: {
                preset: StelaPreset,
                options: {
                    darkModeSelector: '[data-color-scheme="dark"]',
                    // Optimus en su propia capa; el orden completo se declara en styles/tailwind.css.
                    cssLayer: { name: 'optimus', order: 'theme, base, vendor, optimus, app, utilities' }
                }
            }
        }),
        importProvidersFrom(
            TranslateModule.forRoot({
                defaultLanguage: 'es',
            })
        ),
        ...provideTranslateHttpLoader({ prefix: './api/lang/i18n/', suffix: '/json' }),
        Table,
        TableService,
        MessageService,
        ConfirmationService,
        // Providers migrated from CoreModule
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        // Providers migrated from SharedModule (pipes used as injectable services)
        DatePipe,
        FullnamePipe,
        ContrastColorPipe,
        GridColumnDataPipe,
        // Providers migrated from EventsModule (pipes used as injectable services)
        GuestAccessStatusPipe,
        GuestAccessStatusColorPipe,
        GuestConfirmationStatusTagPipe,
        GuestGroupFullnamePipe,
        {
            provide: APP_INITIALIZER,
            useFactory: initializeApp,
            deps: [Optimus, TranslateService],
            multi: true
        }
    ]
})
    .catch(err => {
        // Bootstrap errors are critical - log to console and monitor
        console.error('Application bootstrap failed:', err);
    });
